import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { PartnerCompanyCard } from "../components/PartnerCompanyCard";
import { PartnerPurchaseDialog } from "../components/PartnerPurchaseDialog";
import { MyPartnerPurchases } from "../components/MyPartnerPurchases";
import { Handshake, Building2 } from "lucide-react";

const Filters = ({ data, state, setState, type, setType }) => (
  <div className="space-y-3 mb-8" data-testid="partner-filters">
    <select value={state} onChange={(e) => setState(e.target.value)} data-testid="partner-filter-state"
            className="h-10 rounded-md border border-border bg-background px-3 text-sm">
      <option value="">Todos os Estados</option>
      {data.states.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
    <div className="flex gap-2 overflow-x-auto pb-1">
      {["", ...data.categories].map((c) => (
        <button key={c || "all"} type="button" onClick={() => setType(c)} data-testid={`partner-filter-type-${c || "todos"}`}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm border transition-colors ${type === c ? "gold-btn border-transparent" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {c || "Todos"}
        </button>
      ))}
    </div>
  </div>
);

export default function Partners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("aba") === "compras" ? "compras" : "lista";
  const [data, setData] = useState(null);
  const [state, setState] = useState("");
  const [type, setType] = useState("");
  const [buying, setBuying] = useState(null);
  const [balance, setBalance] = useState(0);
  const [mine, setMine] = useState([]);
  const authed = !!user;
  const canBuy = authed && (user.account_type === "buyer" || user.activation_status === "approved");

  useEffect(() => {
    const params = {};
    if (state) params.state = state;
    if (type) params.service_type = type;
    api.get("/partner-companies", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], states: [], categories: [] }));
  }, [state, type]);
  const loadMine = useCallback(() => {
    if (!authed) return;
    api.get("/wallet").then((r) => setBalance(r.data.balance)).catch(() => {});
    api.get("/partner-purchases").then((r) => setMine(r.data)).catch(() => {});
  }, [authed]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const onBuy = (c) => (canBuy ? setBuying(c) : navigate(authed ? "/dashboard" : "/login"));

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-28 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <span className="section-eyebrow">Rede de parceiros</span>
            <h1 className="font-display text-3xl sm:text-5xl font-bold mt-4">Empresas <span className="gold-text">Parceiras</span></h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl">Compre com empresas e profissionais da rede e receba parte do desconto de volta no seu saldo.</p>
          </div>
          <Button className="gold-btn" onClick={() => navigate(authed ? "/seja-parceiro" : "/cadastro?tipo=comprador")} data-testid="become-partner-cta">
            <Handshake className="h-4 w-4 mr-2" /> Seja uma Empresa Parceira
          </Button>
        </div>
        {authed && (
          <div className="inline-flex rounded-lg border border-border p-1 mb-6" role="tablist">
            {[["lista", "Empresas"], ["compras", `Minhas compras (${mine.length})`]].map(([v, l]) => (
              <button key={v} role="tab" aria-selected={tab === v} onClick={() => setSp(v === "compras" ? { aba: "compras" } : {})}
                      className={`px-4 py-1.5 rounded-md text-sm ${tab === v ? "gold-btn" : "text-muted-foreground"}`} data-testid={`partners-tab-${v}`}>{l}</button>
            ))}
          </div>
        )}
        {tab === "compras" ? <MyPartnerPurchases items={mine} onChanged={loadMine} /> : !data ? <p className="text-muted-foreground">Carregando...</p> : (
          <>
            <Filters data={data} state={state} setState={setState} type={type} setType={setType} />
            {data.items.length === 0 ? (
              <Card className="p-10 text-center" data-testid="partners-empty"><Building2 className="h-10 w-10 mx-auto text-amber-400" />
                <p className="mt-3 text-sm text-muted-foreground">{state || type ? "Nenhuma empresa com esses filtros." : "Em breve as primeiras empresas parceiras."}</p></Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
                {data.items.map((c) => <PartnerCompanyCard key={c.id} c={c} onBuy={onBuy} canBuy={canBuy} />)}
              </div>
            )}
          </>
        )}
      </div>
      <PartnerPurchaseDialog company={buying} balance={balance} onClose={() => setBuying(null)} onDone={loadMine} />
    </div>
  );
}
